import AutoSizer from 'react-virtualized-auto-sizer';
import { Box, Typography, useTheme } from '@mui/material';
import { InfiniteTableProps, InfiniteTableRendererProps } from './types';
import { BLANK } from '../../utils/global';
import { headerRenderer, infiniteTableRenderer } from './renderers';

export const TABLE_ROW_HEIGHT   = 36;
export const TABLE_COL_SPACING  = 12;
export const TABLE_MARGIN_LEFT  = 24;
export const TABLE_MARGIN_RIGHT = 24;

export const MIN_BATCH_SIZE = 50;
export const MAX_BATCH_SIZE = 200;

const InfiniteTable = <T extends object>({
  width,
  height,
  headerHeight = 54,
  rowHeight = TABLE_ROW_HEIGHT,
  marginLeft = TABLE_MARGIN_LEFT,
  marginRight = TABLE_MARGIN_RIGHT,
  columnSpacing = TABLE_COL_SPACING,
  columns,
  items,
  totalItems,
  selectedItemIdx = -1,
  minBatchSize = MIN_BATCH_SIZE,
  maxBatchSize = MAX_BATCH_SIZE,
  threshold = 20,
  isLoading,
  noResultsText = BLANK,
  sorter,
  rowRenderer,
  getRowTextColour,
  loadMoreItems,
  // checkItemLoaded,
  onClickRow,
  onSortColumn,
  sx,
}: InfiniteTableProps<T>) => {
  const theme = useTheme();

  const rowWidth = width || columns.reduce<number>((total, column) => {
    return columns ? total + column.width + columnSpacing : total;
  }, marginLeft + marginRight - columnSpacing);

  const tableRendererProps: InfiniteTableRendererProps<T> = {
    columns,
    items,
    totalItems,
    isLoading,
    selectedItemIdx,
    minBatchSize,
    maxBatchSize,
    threshold,
    tableHeight: 0,
    rowWidth,
    rowHeight,
    columnSpacing,
    ml: marginLeft,
    mr: marginRight,
    theme,
    rowRenderer,
    getRowTextColour,
    loadMoreItems,
    // checkItemLoaded,
    onClickRow,
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
      overflow: 'auto hidden',
      ...sx,
    }}>
      {headerRenderer({
        columns,
        rowWidth,
        headerHeight,
        columnSpacing,
        ml: marginLeft,
        mr: marginRight,
        sorter,
        theme,
        onSortColumn,
      })}
      
      {(totalItems < 0) ? (         // Total < 0 -> No items loaded yet -> Show loading icon
        <Typography variant='h6' sx={{ width: rowWidth - marginLeft - marginRight, ml: `${marginLeft}px`, mr: `${marginRight}px`, mt: 1 }}>
          Loading...
        </Typography>
      ) : (totalItems === 0) ? (    // Total = 0 -> No items match filter
        <Typography variant='h6' sx={{ width: rowWidth - marginLeft - marginRight, ml: `${marginLeft}px`, mr: `${marginRight}px`, mt: 1 }}>
          {noResultsText}
        </Typography>
      ) : height ? infiniteTableRenderer({ ...tableRendererProps, tableHeight: height }) : (    // Total > 0 -> Show items
        <Box sx={{ flex: '1 1 auto' }}>
          <AutoSizer>
            {({ height }) => infiniteTableRenderer({ ...tableRendererProps, tableHeight: height })}
          </AutoSizer>
        </Box>
      )}
    </Box>
  );
}

export default InfiniteTable;